<template>
  <div>
    <div class="d-flex mb-3">
      <h1 class="h4 primary--text">
        {{ $t('views.consolidator_asn_receiving.consolidator_asn_receiving') }}
      </h1>
    </div>
    <main class="bg-white pa-6">
      <div class="asn-receiving">
        <div class="input-block input-block_white">
          <label class="input-block__label">
            {{ $t('common.asn') }}
          </label>
          <v-autocomplete
            v-model="asn"
            :loading="loadingAsnList"
            :items="asnList"
            :search-input.sync="asnSearch"
            :item-text="asnText"
            :placeholder="$t('common.type_to_search')"
            :disabled="Boolean(asnReceiving.id)"
            return-object
            no-filter
            hide-no-data
            solo
            @input="getAsnReceiving"
          >
            <template
              slot="item"
              slot-scope="data"
            >
              <span v-html="asnMatchText(data.item)" />
            </template>
          </v-autocomplete>
          <app-loader v-if="loading" />
          <template v-else-if="asnReceiving.id">
            <div class="input-block input-block_white">
              <label class="input-block__label">
                {{ $t('views.consolidator_asn_receiving.scan_or_select_serial') }}
              </label>
              <v-text-field
                ref="serialNumber"
                v-model="serialNumber"
                solo
                :readonly="asnReceiving.isReceived"
                @input="checkSerialNumber"
              />
            </div>
            <v-data-table
              :items="asnReceiving.packing"
              :headers="headers"
              disable-pagination
              disable-sort
              hide-default-footer
            >
              <template v-slot:item.ok="{ item }">
                <v-checkbox
                  v-model="item.ok"
                  :readonly="asnReceiving.isReceived"
                  @change="togglePallet(item, 'nok', $event)"
                />
              </template>
              <template v-slot:item.nok="{ item }">
                <v-checkbox
                  v-model="item.nok"
                  :readonly="asnReceiving.isReceived"
                  @change="togglePallet(item, 'ok', $event)"
                />
              </template>
              <template v-slot:item.notSelected="{ item }">
                <v-checkbox v-model="item.notSelected" />
              </template>
            </v-data-table>
            <div class="mt-7">
              <v-btn
                outlined
                :disabled="loading || saving || asnReceiving.isReceived"
                color="primary"
                class="mr-5"
                @click="save(false)"
              >
                {{ $t('common.save') }}
              </v-btn>
              <v-btn
                :disabled="loading || saving || asnReceiving.isReceived"
                color="primary"
                class="mr-5"
                @click="save(true)"
              >
                {{ $t('views.consolidator_asn_receiving.finish') }}
              </v-btn>
              <v-btn
                outlined
                :disabled="loading || saving"
                color="primary"
                @click="restart"
              >
                {{ $t('views.consolidator_asn_receiving.restart') }}
              </v-btn>
            </div>
          </template>
        </div>
      </div>
    </main>
    <shipment-notice-errors-modal
      v-model="isErrorsModalShown"
      :error-list="errorsModalList"
      :title="$t('views.shipment_notice_edit.validation_errors')"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { mongoRegexI } from '@/utils/mongo-regex';
import AppLoader from '@/components/common/AppLoader.vue';
import ShipmentNoticeErrorsModal from '@/components/asn/ShipmentNoticeErrorsModal.vue';

export default {
  name: 'ConsolidatorAsnReceiving',

  components: {
    AppLoader,
    ShipmentNoticeErrorsModal,
  },

  data() {
    return {
      asnList: [],
      loadingAsnList: false,
      asnSearch: '',
      asn: {},

      asnReceiving: {},
      serialNumber: '',
      headers: [
        {
          text: this.$t('common.pallet'),
          value: 'boxSerialNumberLevel1',
        },
        {
          text: 'OK',
          value: 'ok',
        },
        {
          text: 'NOK',
          value: 'nok',
        },
      ],

      loading: false,
      saving: false,

      isErrorsModalShown: false,
      errorsModalList: [],
    };
  },

  computed: {
    ...mapState({
      user: 'user',
      supplierData: 'supplier',
    }),
  },

  watch: {
    asnSearch(value) {
      if (value) {
        this.getAsnList(value);
      }
    },
  },

  methods: {
    async getAsnList(str) {
      this.loadingAsnList = true;
      let query;

      const numberPositionInQR = str.lastIndexOf('ASNN');
      const datePositionInQR = str.lastIndexOf('ASND');
      const shippingPositionInQR = str.lastIndexOf('ASNS');
      const isQR = numberPositionInQR !== -1
        && datePositionInQR !== -1
        && shippingPositionInQR !== -1;

      if (isQR) {
        const date = str.slice(datePositionInQR + 4, shippingPositionInQR);
        query = {
          ...(this.user.asdb ? { 'plant.asdb': this.user.asdb } : {}),
          number: str.slice(numberPositionInQR + 4, datePositionInQR),
          date: {
            $gte: this.$moment(date, 'DD.MM.YYYY'),
            $lte: this.$moment(date, 'DD.MM.YYYY').endOf('day'),
          },
        };
      } else {
        query = {
          ...(this.user.asdb ? { 'plant.asdb': this.user.asdb } : {}),
          $or: [
            { number: mongoRegexI(str) },
            { 'packing.boxSerialNumberLevel1': mongoRegexI(str) },
          ],
        };
      }

      try {
        const { data = {} } = await this.$http.get('/asn', {
          params: { query, sort: '-date' },
        });
        this.asnList = data.rows || [];
      } catch (err) {
        this.$http.mockApiError(err);
      } finally {
        this.loadingAsnList = false;
      }

      if (isQR && this.asnList.length === 1) {
        [this.asn] = this.asnList;
        await this.getAsnReceiving();
      }
    },

    asnText(asn) {
      return `${asn.number} [${asn.supplier ? asn.supplier.asdb : ''}] ${this.$t('common.dated')} ${this.$moment(asn.date).format('L')}`;
    },

    asnMatchText(asn) {
      const regExp = new RegExp(`(?<pre>.*?)(?<dat>${this.asnSearch})(?<post>.*)`, 'i');

      const text = this.asnText(asn);

      const packingString = (asn.packing || []).reduce((acc, pack) => {
        const m = pack && pack.boxSerialNumberLevel1 && pack.boxSerialNumberLevel1.match(regExp);
        if (m) {
          acc.push(`${m.groups.pre}<b>${m.groups.dat}</b>${m.groups.post}`);
        }

        return acc;
      }, []).join(',&nbsp;');

      const shortPackingString = (packingString.length > 40) ? `${packingString.slice(0, 40)}...` : packingString;

      return `${text}${shortPackingString && ',&nbsp;'}${shortPackingString}`;
    },

    async getAsnReceiving() {
      this.loading = true;
      try {
        const { data } = await this.$http.get(`receiving/get-by-asn-id/${this.asn.id}`);
        this.asnReceiving = {
          ...data,
          packing: data.packing.map((position) => ({
            ...position,
            ok: position.status === 'ok',
            nok: position.status === 'nok',
          })),
        };
      } catch (err) {
        this.$http.mockApiError(err);
      } finally {
        this.loading = false;
        if (this.asnReceiving.id) {
          await this.$nextTick();
          this.$refs.serialNumber.focus();
        }
      }
    },

    checkSerialNumber() {
      if (!this.serialNumber.startsWith('S') || this.serialNumber.length < 2) {
        return;
      }
      const found = this.asnReceiving.packing.find(
        (position) => position.boxSerialNumberLevel1 === this.serialNumber.slice(1),
      );
      if (found) {
        found.ok = true;
        found.nok = false;
        this.serialNumber = '';
      }
    },

    togglePallet(item, reverseField, value) {
      if (value) {
        item[reverseField] = false;
      }
    },

    async save(isReceived) {
      const asnReceiving = {
        ...this.asnReceiving,
        isReceived,
        packing: this.asnReceiving.packing.map((position) => {
          const { boxSerialNumberLevel1, ok, nok } = position;
          let status;
          if (ok) {
            status = 'ok';
          } else if (nok) {
            status = 'nok';
          } else {
            status = '-';
          }
          return { boxSerialNumberLevel1, status };
        }),
      };

      this.saving = true;
      try {
        const { data } = await this.$http.put(`/receiving/${this.asnReceiving.id}`, asnReceiving);
        this.asnReceiving = {
          ...data,
          packing: data.packing.map((position) => ({
            ...position,
            ok: position.status === 'ok',
            nok: position.status === 'nok',
          })),
        };
      } catch (error) {
        if (error.translatedErrorList) {
          this.errorsModalList = error.translatedErrorList;
          this.isErrorsModalShown = true;
        } else {
          this.$toast(this.$t('views.shipment_notice_edit.validation_errors'), {
            title: this.$t('toast.danger'),
            variant: 'danger',
          });
        }
      } finally {
        this.saving = false;
      }
    },

    restart() {
      this.asn = {};
      this.asnReceiving = {};
      this.serialNumber = '';
    },
  },
};
</script>

<style lang="scss" scoped>
.asn-receiving {
  max-width: 1082px;
}
</style>
